"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getDevAdvancedCopy } from "@/lib/dev-advanced-copy";
import { getToolUiCopy } from "@/lib/tool-ui-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function ColorPicker() {
  const [color, setColor] = useState("#3b82f6");
  const [rgb, setRgb] = useState({ r: 59, g: 130, b: 246 });
  const [hsl, setHsl] = useState({ h: 217, s: 91, l: 60 });
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const copy = getDevAdvancedCopy(locale);
  const ui = getToolUiCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "color-picker") ?? tools[0],
    locale
  );

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 0, b: 0 };
  };

  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  const handleColorChange = (newColor: string) => {
    setColor(newColor);
    const rgbValues = hexToRgb(newColor);
    setRgb(rgbValues);
    setHsl(rgbToHsl(rgbValues.r, rgbValues.g, rgbValues.b));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatOptions = [
    { label: "HEX", value: color, icon: "mdi:pound" },
    { label: "RGB", value: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`, icon: "mdi:palette" },
    { label: "HSL", value: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`, icon: "mdi:invert-colors" },
  ];

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:eyedropper" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

       <div className="rounded-[30px] border-4 border-positivus-dark bg-white p-6 shadow-[0_8px_0_0_#191A23]">
          <div className="space-y-6">
              <div
                className="w-full h-48 rounded-[20px] shadow-inner border-2 border-positivus-dark/10 transition-colors duration-300"
                style={{ backgroundColor: color }}
              />
              <div className="relative">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="w-full h-16 rounded-[15px] cursor-pointer border-2 border-positivus-dark/20 p-1 hover:border-positivus-dark transition-all"
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-positivus-dark font-bold bg-white/20 backdrop-blur-[1px] rounded-[15px]">
                      {copy.clickToChangeColor}
                  </div>
              </div>
          </div>
       </div>

      <div className="space-y-4">
        <p className="text-lg font-bold text-positivus-dark">{copy.colorCodes}</p>
        <div className="grid gap-4">
            {formatOptions.map((format) => (
            <div
                key={format.label}
                className="flex items-center justify-between rounded-[15px] border-2 border-positivus-dark/10 bg-white p-5 hover:border-positivus-dark hover:shadow-[0_4px_0_0_#191A23] transition-all group"
            >
                <div className="flex items-center gap-4">
                <div className="rounded-full bg-positivus-green/10 p-2">
                    <Icon icon={format.icon} className="h-5 w-5 text-positivus-dark" />
                </div>
                <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{format.label}</p>
                    <p className="font-mono text-lg font-bold text-positivus-dark">{format.value}</p>
                </div>
                </div>
                <button
                onClick={() => copyToClipboard(format.value)}
                className="opacity-0 group-hover:opacity-100 flex items-center gap-2 rounded-[10px] bg-positivus-dark px-4 py-2 text-sm font-bold text-white hover:bg-positivus-green hover:text-positivus-dark transition-all"
                >
                <Icon icon={uiIcons.copy} className="h-4 w-4" />
                {ui.copy}
                </button>
            </div>
            ))}
        </div>
      </div>

      <div className="rounded-[20px] border-2 border-positivus-dark/10 bg-white p-6">
        <p className="text-lg font-bold text-positivus-dark mb-4">{copy.rgbValues}</p>
        <div className="grid grid-cols-3 gap-4">
            <div>
            <label className="mb-2 block text-xs font-bold text-red-500 uppercase text-center">{copy.red}</label>
            <input
                type="number"
                min="0"
                max="255"
                value={rgb.r}
                onChange={(e) => {
                const r = parseInt(e.target.value) || 0;
                const newRgb = { ...rgb, r };
                setRgb(newRgb);
                const hex = `#${r.toString(16).padStart(2, '0')}${newRgb.g.toString(16).padStart(2, '0')}${newRgb.b.toString(16).padStart(2, '0')}`;
                handleColorChange(hex);
                }}
                className="w-full rounded-[10px] border-2 border-red-200 bg-red-50 px-3 py-3 text-center font-bold text-red-600 focus:border-red-500 focus:outline-none focus:ring-0"
            />
            </div>
            <div>
            <label className="mb-2 block text-xs font-bold text-green-500 uppercase text-center">{copy.green}</label>
            <input
                type="number"
                min="0"
                max="255"
                value={rgb.g}
                onChange={(e) => {
                const g = parseInt(e.target.value) || 0;
                const newRgb = { ...rgb, g };
                setRgb(newRgb);
                const hex = `#${newRgb.r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${newRgb.b.toString(16).padStart(2, '0')}`;
                handleColorChange(hex);
                }}
                className="w-full rounded-[10px] border-2 border-green-200 bg-green-50 px-3 py-3 text-center font-bold text-green-600 focus:border-green-500 focus:outline-none focus:ring-0"
            />
            </div>
            <div>
            <label className="mb-2 block text-xs font-bold text-blue-500 uppercase text-center">{copy.blue}</label>
            <input
                type="number"
                min="0"
                max="255"
                value={rgb.b}
                onChange={(e) => {
                const b = parseInt(e.target.value) || 0;
                const newRgb = { ...rgb, b };
                setRgb(newRgb);
                const hex = `#${newRgb.r.toString(16).padStart(2, '0')}${newRgb.g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                handleColorChange(hex);
                }}
                className="w-full rounded-[10px] border-2 border-blue-200 bg-blue-50 px-3 py-3 text-center font-bold text-blue-600 focus:border-blue-500 focus:outline-none focus:ring-0"
            />
            </div>
        </div>
      </div>
    </div>
  );
}
